/**
 * Premium Section Renderer — Phase 1+2+3 EXTENSIONS dispatch
 *
 * Per-block-type renderers live under `./ext/*.js` for maintainability
 * (each file < 200 lines). This file is a thin router.
 *
 * The main renderer (PremiumSectionRenderer.js) calls `renderExt(block, theme)`
 * as a fall-through BEFORE its own switch. If we return a React element we win,
 * else the parent renderer continues as normal.
 */
import React from 'react';
import { HeroExt }            from './ext/hero';
import { GalleryExt }         from './ext/gallery';
import { CtaExt }             from './ext/cta';
import { TestimonialsExt }    from './ext/testimonials';
import { TeamExt }            from './ext/team';
import { FeaturesExt }        from './ext/features';
import { StatsExt }           from './ext/stats';
import { EditorialExt }       from './ext/editorial';
import { SplitExt }           from './ext/split';
import { ServicesExt }        from './ext/services';
import { LogosBlockExt }      from './ext/logos';
import { DataTableBlock }     from './ext/dataTable';
import { ProductGridBlock }   from './ext/productGrid';
import { ProductDetailBlock } from './ext/productDetail';
import { CartTableBlock }     from './ext/cartTable';
import { FormExt }            from './ext/form';
import { EventsExt }          from './ext/events';
// Iteration 143 — net-new block types
import { FooterBlock }        from './ext/footer';
import { FaqBlock }           from './ext/faq';
import { ContactBlock }       from './ext/contact';
// Iteration 138 — agency long-scroll blocks
import { AgencyExt, AGENCY_BLOCK_TYPES } from './ext/agency';

export const EXT_BLOCK_TYPES = new Set([
  'data_table', 'product_grid', 'product_detail', 'cart_table',
  // Iter 143 — new
  'footer', 'faq', 'contact',
  // Iter 138 — agency
  ...AGENCY_BLOCK_TYPES,
]);

// Per-block-type allow-lists. Listing a layout here makes renderExt claim it
// (preventing the legacy renderer from falling through). Keep in sync with
// the layout IDs implemented in each ext/*.js file.
const EXT_LAYOUTS = {
  hero:         new Set([
    'mountain-card','sunset-form','video-modal','water-fullbleed','portrait-grid','card-on-photo',
    'dark-form-capture','peacock-fullbleed','illustration-hero','login-split','laptop-footer-features',
  ]),
  gallery:      new Set([
    'mosaic-asymmetric','violet-bg-masonry','filterable','with-swatches',
    'alternating-rows','last-works-link',
    'carousel-scroll','marquee-gallery',
    // Iter 189c Phase B — new premium Gallery variants
    'masonry-editorial','full-bleed-scroll',
  ]),
  cta:          new Set([
    'pill-bar','social-pill-row','mountain-overlay','video-modal-cta','phone-on-sunset','balloons-asymmetric',
    'app-store-badges','slim-promo-bar','integration-cards-cta','sunset-promo-card',
    'white-bordered-card','video-strip-vertical',
  ]),
  testimonials: new Set([
    'two-by-two-grid','violet-2up','happy-clients-quadgrid',
    'single-quote-portrait','author-bottom-card',
    // Iter 189c Phase B — new premium Testimonials variants
    'wall-of-love','interview-hero',
  ]),
  team:         new Set([
    'palm-leaf-bg','square-cards','violet-bg',
    // Iter 189c Phase B — new premium Team variants
    'org-chart-tree','founder-spotlight',
  ]),
  features:     new Set([
    'icon-row','integration-cards','phone-mockup','laptop-mockup','six-icon-violet',
    'two-by-two-cards','six-feature-checklist','three-stat-block',
    // Iter 178f · Services Section Audit
    'icon-features-4up','process-steps-horizontal','large-feature-dark',
    'tabbed-services-content','cards-large-2x2',
  ]),
  stats:        new Set(['dark-violet-card','side-stats','stats-with-mockup']),
  editorial:    new Set(['dark-violet-block','sub-card']),
  split:        new Set(['split-form','split-overlay']),
  services:     new Set([
    'pricing-tabs','pricing-comparison-grid',
    'pricing-dark-hero','pricing-highlighted-middle','pricing-membership',
    // Iter 189c Phase B — new premium Pricing variants
    'pricing-with-testimonial','pricing-annual-toggle-visual',
  ]),
  logos:        new Set(['logo-cards-grid','violet-trust-bar','row-grey','client-logos-wall']),
  form:         new Set(['auth-signup','auth-signin','payment-method','billing-account','multi-step']),
  // Iter 189c Phase B — new premium Events variants
  events:       new Set(['agenda-list','featured-hero']),
};

export function renderExt(block, theme, onEdit, onMediaPick) {
  if (!block || !block.type) return null;
  const t = block.type;
  const E = { onEdit, onMediaPick };

  // NEW block types — always handled here
  if (t === 'data_table')     return <DataTableBlock block={block} theme={theme} E={E} />;
  if (t === 'product_grid')   return <ProductGridBlock block={block} theme={theme} E={E} />;
  if (t === 'product_detail') return <ProductDetailBlock block={block} theme={theme} E={E} />;
  if (t === 'cart_table')     return <CartTableBlock block={block} theme={theme} E={E} />;
  if (t === 'footer')         return <FooterBlock block={block} theme={theme} E={E} />;
  if (t === 'faq')            return <FaqBlock block={block} theme={theme} E={E} />;
  if (t === 'contact')        return <ContactBlock block={block} theme={theme} E={E} />;
  if (AGENCY_BLOCK_TYPES.has(t)) return <AgencyExt block={block} theme={theme} E={E} />;

  // Layout-extension variants on EXISTING block types
  const layout = block.layout;
  if (!layout) return null;
  const allow = EXT_LAYOUTS[t];
  if (!allow || !allow.has(layout)) return null;

  if (t === 'hero')         return <HeroExt block={block} theme={theme} E={E} />;
  if (t === 'gallery')      return <GalleryExt block={block} theme={theme} E={E} />;
  if (t === 'cta')          return <CtaExt block={block} theme={theme} E={E} />;
  if (t === 'testimonials') return <TestimonialsExt block={block} theme={theme} E={E} />;
  if (t === 'team')         return <TeamExt block={block} theme={theme} E={E} />;
  if (t === 'features')     return <FeaturesExt block={block} theme={theme} E={E} />;
  if (t === 'stats')        return <StatsExt block={block} theme={theme} E={E} />;
  if (t === 'editorial')    return <EditorialExt block={block} theme={theme} E={E} />;
  if (t === 'split')        return <SplitExt block={block} theme={theme} E={E} />;
  if (t === 'services')     return <ServicesExt block={block} theme={theme} E={E} />;
  if (t === 'logos')        return <LogosBlockExt block={block} theme={theme} E={E} />;
  if (t === 'form')         return <FormExt block={block} theme={theme} E={E} />;
  if (t === 'events')       return <EventsExt block={block} theme={theme} E={E} />;
  return null;
}
